import { useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Trash2, Search, Edit3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'

// Module-level cache so we don't refetch the catalogue every time the
// modal opens (the file is ~37 KB / 792 entries).
let _LR_NAMES_CACHE: Record<string, string> | null = null
let _LR_NAMES_PROMISE: Promise<Record<string, string>> | null = null

async function fetchLrNames(): Promise<Record<string, string>> {
  if (_LR_NAMES_CACHE) return _LR_NAMES_CACHE
  if (!_LR_NAMES_PROMISE) {
    _LR_NAMES_PROMISE = fetch('/api/yandex-lr-names')
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then((d: Record<string, string>) => {
        _LR_NAMES_CACHE = d || {}
        return _LR_NAMES_CACHE
      })
  }
  return _LR_NAMES_PROMISE
}

type Row = { lr: string; weight: number }

function parseValue(raw: string): Row[] {
  if (!raw) return []
  const out: Row[] = []
  for (const tok of raw.split(/[;,\s]+/).map(s => s.trim()).filter(Boolean)) {
    let lr = tok
    let w = 1
    const colon = tok.indexOf(':')
    if (colon >= 0) {
      lr = tok.slice(0, colon).trim()
      const wraw = tok.slice(colon + 1).trim()
      const wn = Number(wraw)
      if (Number.isFinite(wn) && wn > 0) w = wn
    }
    if (/^\d+$/.test(lr)) out.push({ lr, weight: w })
  }
  return out
}

function serializeValue(rows: Row[]): string {
  return rows
    .filter(r => /^\d+$/.test(r.lr) && r.weight > 0)
    .map(r => (r.weight === 1 ? r.lr : `${r.lr}:${r.weight}`))
    .join(';')
}

export function CustomRegionsField({
  value,
  onChange,
}: {
  value: string
  onChange: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState<Row[]>(parseValue(value))
  const [names, setNames] = useState<Record<string, string>>(_LR_NAMES_CACHE || {})

  useEffect(() => {
    if (!_LR_NAMES_CACHE) {
      void fetchLrNames().then(setNames)
    }
  }, [])

  // Re-sync rows from external value when modal is opened (so the user
  // sees the current persisted state).
  useEffect(() => {
    if (open) setRows(parseValue(value))
  }, [open, value])

  const summary = useMemo(() => {
    const parsed = parseValue(value)
    if (!parsed.length) return ''
    const labels = parsed.slice(0, 3).map(r => names[r.lr] || `LR ${r.lr}`)
    const tail = parsed.length > 3 ? ` +${parsed.length - 3}` : ''
    return `${parsed.length} регионов · ${labels.join(', ')}${tail}`
  }, [value, names])

  const addRow = () => setRows(r => [...r, { lr: '', weight: 1 }])
  const updateRow = (i: number, patch: Partial<Row>) =>
    setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)))
  const removeRow = (i: number) => setRows(r => r.filter((_, idx) => idx !== i))

  const totalWeight = useMemo(
    () => rows.reduce((s, r) => s + (Number.isFinite(r.weight) && r.weight > 0 ? r.weight : 0), 0),
    [rows]
  )

  const save = () => {
    const cleaned = rows
      .map(r => ({ lr: r.lr.trim(), weight: r.weight }))
      .filter(r => /^\d+$/.test(r.lr) && r.weight > 0)
    onChange(serializeValue(cleaned))
    setOpen(false)
  }

  return (
    <div className="flex flex-col gap-1.5 min-w-0">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full max-w-80 justify-start gap-2 text-left"
        onClick={() => setOpen(true)}
      >
        <Edit3 className="h-3.5 w-3.5 shrink-0 opacity-70" />
        <span className="truncate text-xs">
          {summary || 'Настроить регионы и шансы…'}
        </span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b">
            <DialogTitle>Свои регионы (LR)</DialogTitle>
            <DialogDescription>
              Список Yandex LR с весами. Чем больше вес, тем чаще будет выбран регион.
              Поиск работает по названию города или коду LR. Веса относительные —
              <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[10px]">213:50</code>
              и
              <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[10px]">213:5</code>
              дают тот же результат, что и
              <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[10px]">213:10</code>
              и
              <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[10px]">213:1</code>.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
            {rows.length === 0 && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                Регионы не добавлены. Нажмите «Добавить регион» снизу.
              </div>
            )}
            <div className="flex flex-col gap-2">
              {rows.map((row, i) => (
                <RegionRow
                  key={i}
                  row={row}
                  names={names}
                  totalWeight={totalWeight}
                  onChange={patch => updateRow(i, patch)}
                  onRemove={() => removeRow(i)}
                />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={addRow} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Добавить регион
              </Button>
              <BulkPasteButton onParsed={(newRows) => {
                setRows(prev => {
                  const existing = new Map(prev.map(r => [r.lr, r]))
                  for (const nr of newRows) {
                    if (existing.has(nr.lr)) {
                      existing.get(nr.lr)!.weight = nr.weight
                    } else {
                      existing.set(nr.lr, nr)
                    }
                  }
                  return [...existing.values()]
                })
              }} />
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t flex flex-row items-center justify-between gap-2">
            <div className="text-xs text-muted-foreground">
              {rows.length > 0 && (
                <>Всего: {rows.length} · сумма весов: <span className="tabular-nums">{totalWeight}</span></>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button type="button" onClick={save}>
                Сохранить
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function BulkPasteButton({ onParsed }: { onParsed: (rows: Row[]) => void }) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')

  const parsed = useMemo(() => {
    if (!text.trim()) return []
    const out: Row[] = []
    for (const line of text.split(/[\n\r]+/)) {
      for (const tok of line.split(/[;,\s]+/).map(s => s.trim()).filter(Boolean)) {
        let lr = tok
        let w = 1
        const colon = tok.indexOf(':')
        if (colon >= 0) {
          lr = tok.slice(0, colon).trim()
          const wn = Number(tok.slice(colon + 1).trim())
          if (Number.isFinite(wn) && wn > 0) w = wn
        }
        if (/^\d+$/.test(lr)) out.push({ lr, weight: w })
      }
    }
    return out
  }, [text])

  return (
    <>
      <Button type="button" variant="ghost" size="sm" onClick={() => { setOpen(true); setText('') }} className="gap-1.5 text-xs text-muted-foreground">
        Вставить списком
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Вставить регионы списком</DialogTitle>
            <DialogDescription>
              Формат: <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[10px]">213</code> или{' '}
              <code className="mx-1 rounded bg-muted px-1 py-0.5 text-[10px]">213:5</code> (с весом).
              Разделители: пробел, запятая, точка с запятой, перенос строки.
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder={'213:50\n2:30\n10174:10\n54\n65'}
            className="w-full min-h-[140px] rounded-md border bg-background px-3 py-2 text-sm font-mono resize-y"
            spellCheck={false}
          />
          {parsed.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Распознано: <strong>{parsed.length}</strong> регионов
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Отмена</Button>
            <Button type="button" disabled={parsed.length === 0} onClick={() => { onParsed(parsed); setOpen(false) }}>
              Добавить {parsed.length > 0 && `(${parsed.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function RegionRow({
  row,
  names,
  totalWeight,
  onChange,
  onRemove,
}: {
  row: Row
  names: Record<string, string>
  totalWeight: number
  onChange: (patch: Partial<Row>) => void
  onRemove: () => void
}) {
  const [query, setQuery] = useState(row.lr ? row.lr : '')
  const [showSuggest, setShowSuggest] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  // Sync query when row.lr changes externally.
  useEffect(() => {
    setQuery(row.lr || '')
  }, [row.lr])

  // Close suggestions on outside click.
  useEffect(() => {
    if (!showSuggest) return
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggest(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showSuggest])

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      // Show top popular LRs as default suggestions.
      const popular = ['213', '2', '10174', '54', '65', '47', '38', '39', '35', '51']
      return popular.filter(lr => names[lr]).map(lr => ({ lr, name: names[lr] }))
    }
    if (/^\d+$/.test(q)) {
      const out: { lr: string; name: string }[] = []
      // Exact match first.
      if (names[q]) out.push({ lr: q, name: names[q] })
      for (const [lr, name] of Object.entries(names)) {
        if (lr !== q && lr.startsWith(q)) out.push({ lr, name })
        if (out.length >= 200) break
      }
      return out
    }
    const out: { lr: string; name: string }[] = []
    for (const [lr, name] of Object.entries(names)) {
      if (name.toLowerCase().includes(q)) out.push({ lr, name })
      if (out.length >= 200) break
    }
    return out
  }, [query, names])

  const label = row.lr ? names[row.lr] || `Неизвестный LR ${row.lr}` : ''
  const sharePct = totalWeight > 0 && row.weight > 0
    ? Math.round((row.weight / totalWeight) * 100)
    : 0

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border/40 bg-muted/30 p-2">
      <div ref={wrapRef} className="relative flex-1 min-w-[200px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={query}
            onFocus={() => setShowSuggest(true)}
            onChange={e => {
              setQuery(e.target.value)
              setShowSuggest(true)
              // If user typed pure numeric LR, pre-commit it.
              if (/^\d+$/.test(e.target.value)) {
                onChange({ lr: e.target.value })
              }
            }}
            placeholder="Город или LR (например: Москва или 213)"
            className="pl-7 h-9 text-sm"
          />
        </div>
        {showSuggest && suggestions.length > 0 && (
          <div className="absolute z-50 mt-1 w-full max-h-80 overflow-y-auto rounded-md border bg-popover shadow-md">
            {suggestions.map(s => (
              <button
                key={s.lr}
                type="button"
                className="flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-accent"
                onClick={() => {
                  onChange({ lr: s.lr })
                  setQuery(s.name)
                  setShowSuggest(false)
                }}
              >
                <span className="truncate">{s.name}</span>
                <span className="shrink-0 font-mono text-muted-foreground">LR {s.lr}</span>
              </button>
            ))}
          </div>
        )}
        {row.lr && label && !showSuggest && (
          <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
            <span className="font-mono">LR {row.lr}</span>
            {label && <> · {label}</>}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <span className="text-xs text-muted-foreground whitespace-nowrap">Вес</span>
        <Input
          type="number"
          min={1}
          value={row.weight}
          onChange={e => {
            const n = Number(e.target.value)
            if (Number.isFinite(n) && n > 0) onChange({ weight: n })
          }}
          className="w-20 h-9 text-sm tabular-nums"
        />
        <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-right">
          {sharePct ? `${sharePct}%` : '—'}
        </span>
      </div>

      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={onRemove}
        className="h-9 w-9 text-muted-foreground hover:text-destructive"
        title="Удалить"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  )
}
